// Administrative activity stats (admin JWT). Shape mirrors the API exactly:
//   - StatsReport / ActivityBucket → api/src/services/stats-service.ts
//   - the route                    → api/src/http/routes/admin.ts (GET /admin/stats?days=1..90)
//
// This is ADMINISTRATIVE activity (channel/member/tier/Org_ID changes), NOT
// message/traffic volume — the UI must label it accordingly. Success bodies are
// Zod-validated at the boundary so envelope drift fails loud as a ContractError.
import { z } from "zod";
import { apiFetch } from "./client";

export interface ActivityBucket {
  /** UTC calendar date, "YYYY-MM-DD". */
  date: string;
  total: number;
  byAction: Record<string, number>;
}

export interface StatsReport {
  rangeDays: number;
  totals: { channels: number; members: number; events: number };
  /** One bucket per day with activity, ascending. Sparse — no zero-fill. */
  activity: ActivityBucket[];
}

const bucketSchema = z.object({
  date: z.string(),
  total: z.number(),
  byAction: z.record(z.string(), z.number()),
});

const statsSchema = z.object({
  rangeDays: z.number(),
  totals: z.object({ channels: z.number(), members: z.number(), events: z.number() }),
  activity: z.array(bucketSchema),
});

/** Activity report for the caller's org. `days` clamps 1..90 (API default 30). */
export function getStats(days?: number, signal?: AbortSignal): Promise<StatsReport> {
  const q = days ? `?days=${encodeURIComponent(days)}` : "";
  return apiFetch(`/admin/stats${q}`, { auth: true, signal, schema: statsSchema });
}
